import { getNextStaticProps, is404 } from "@faustjs/next";
import { client, Post } from "client";
import { GetStaticPropsContext } from "next";
import Layout from "layouts/main";
import { useMenu, useScroll } from "hooks";
import {
  CurrentlyReadingBanner,
  SocialShareButtons,
  BrandColorsDivider,
  SeriesBanner,
} from "components";
import slugify from "slugify";
import { useEffect, useState } from "react";
import clsx from "clsx";
export interface PostProps {
  post: Post | Post["preview"]["node"] | null | undefined;
}
import { PopupButton } from "@typeform/embed-react";

export function PostComponent({ post }: PostProps) {
  const { scrollY } = useScroll();
  const headerLinks = useMenu("PRIMARY");
  const footerLinks = useMenu("FOOTER");
  const categories = post?.categories().nodes;
  const [content, setContent] = useState("");
  const [contentsTable, setContentsTable] = useState([]);
  const removeHTMLTags = (str) => str.replace(/<[^>]*>?/gm, "");

  const addIdToHeading = (str) => {
    const heading = removeHTMLTags(str);
    const slug = slugify(heading);
    return str?.replace(heading, `<h2 id="${slug}">${heading}</h2>`);
  };

  const addIdToHeadings = (str) => {
    const arrayOfHeadings = str?.match(/<h2.*?>(.*?)<\/h2>/g);
    if (arrayOfHeadings) {
      arrayOfHeadings.forEach((heading) => {
        str = str.replace(heading, addIdToHeading(heading));
      });
    }
    return str;
  };

  useEffect(() => {
    document.querySelectorAll("h2").forEach((heading) => {
      heading.classList.add("scroll-m-24");
    });
    setContentsTable(post?.content()?.match(/<h2.*?>(.*?)<\/h2>/g));
    setContent(addIdToHeadings(post?.content()));
  }, [post?.content()]);

  const isSeries = post?.postsACF?.series?.__typename;
  return (
    <Layout page={post} headerLinks={headerLinks} footerLinks={footerLinks}>
      {!isSeries && <CurrentlyReadingBanner post={post} />}
      {isSeries && <SeriesBanner post={post} />}

      <section className="section-container w-full mx-auto">
        <div
          className={clsx(
            "hidden lg:block sticky top-16 w-64 p-4 float-left transition-all duration-500 ease-in-out",
            scrollY > 70 ? "opacity-100" : "opacity-0"
          )}
        >
          <h4 className="text-xl font-bold mb-4">Contents</h4>
          <ol className="list-decimal ml-6">
            {contentsTable?.map((heading) => {
              return (
                <li className="mb-2" key={heading}>
                  <a href={`#${slugify(removeHTMLTags(heading))}`}>
                    {removeHTMLTags(heading)}
                  </a>
                </li>
              );
            })}
          </ol>
          <div className="bg-white">
            <div className="mx-auto max-w-7xl py-24 sm:py-32 ">
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                Is your business <br /> ready for{" "}
                <span className="text-blue-dark">structured</span>{" "}
                <span className="text-yellow-dark">content</span>?
              </h2>
              <div className="mt-10 flex items-center gap-x-6">
                <PopupButton
                  id="MFQ5J7EV"
                  className="rounded-md bg-blue-dark px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-dark"
                >
                  Readiness Quiz
                </PopupButton>
              </div>
            </div>
          </div>
        </div>
        <div className="blog-container px-4 lg:px-0 mx-auto">
          <div className="flex flex-col justify-start items-start border-b border-slate-300 mb-8">
            <h1 className="h2">{post.title()}</h1>
            <div className="font-bold text-xs text-black bg-green-light rounded-md max-w-max px-2 py-1 mt-4 mb-8">
              {categories[0]?.name}
            </div>
          </div>
          <div
            className="blog-content font-light"
            dangerouslySetInnerHTML={{
              __html: content ?? "",
            }}
          />
          <SocialShareButtons slug={post.slug} />
        </div>
        <BrandColorsDivider />
      </section>
    </Layout>
  );
}

export default function Page() {
  const { usePost } = client;
  const post = usePost();

  return <PostComponent post={post} />;
}

export async function getStaticProps(context: GetStaticPropsContext) {
  return getNextStaticProps(context, {
    Page,
    client,
    notFound: await is404(context, { client }),
  });
}

export async function getStaticPaths() {
  const url = `${process.env.NEXT_PUBLIC_WORDPRESS_URL}/wp-json/wp/v2/posts?per_page=100`;
  var response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });
  let slugs = [];

  if (response.status === 200) {
    const posts = await response.json();
    slugs = posts?.map((post) => `/blog/${post.slug}`);
  }
  return {
    paths: slugs,
    fallback: "blocking",
  };
}
